// @flow

import { logger, setItem, getQuery } from '../../../../common'

import { MESSAGE_TYPE_SET_ITEM } from '../../../../common/iframeMessageTypes'
import { addQueryToURL } from '../../../../common/url'
import { SHOWN_WELCOME_MESSAGE_IDS_KEY, TRIGGERS, WELCOME_MESSAGE_ACTIVELY_CLOSED_COOKIE_NAME } from '../../../models'
import { requestMessageHistory, sendWelcomeMessageClicked } from '../../../utils/api'
import { postMessageToParent } from '../../../utils/iframe'
import type { RootState } from '../../rootState'
import type { State } from './state'
import { alreadySeenWelcomeMessageIds, getValidExitWelcomeMessage, getWelcomeMessage } from './welcomeMessageQueries'

export const actions = {
  pickWelcomeMessage ({
    commit,
    state,
    rootState
  }: { commit: any, state: State, rootState: RootState }) {
    if (state.welcomeMessages?.length > 0) {
      const welcomeMessage = getWelcomeMessage(state.welcomeMessages, rootState.parentUrl, rootState.device, state.forceShowWelcomeMessagePayload)
      if (!welcomeMessage) return
      logger.info(`Picked welcome message ${welcomeMessage.id} from ${state.welcomeMessages.map(wm => wm.id).join(',')}.`)

      if (welcomeMessage.trigger === TRIGGERS.immediately) {
        logger.info('Showing immediately welcome message...')
        commit('setWelcomeMessage', welcomeMessage)
      } else if (welcomeMessage.trigger === TRIGGERS.timePassed) {
        const timeout = parseInt(welcomeMessage.triggerValue)
        setTimeout(() => {
          if (!rootState.bubble.isOpen) {
            logger.info('Showing timed welcome message...')
            commit('setWelcomeMessage', welcomeMessage)
          } else {
            logger.info('Ignoring timed welcome message because chat bubble is opened...')
          }
        }, timeout * 1000)
      } else if (welcomeMessage.trigger === TRIGGERS.percentScrolled) {
        if (rootState.scrollPercent && rootState.scrollPercent >= welcomeMessage.triggerValue) {
          logger.info('Showing percent scrolled welcome message...')
          commit('setWelcomeMessage', welcomeMessage)
        }
      }
    }
  },
  welcomeMessageClosed ({
    commit,
    getters
  }: { commit: any, getters: any }) {
    const value = new Date().toISOString()
    commit('setClosedByUserAt', value)
    postMessageToParent({
      event: MESSAGE_TYPE_SET_ITEM,
      key: WELCOME_MESSAGE_ACTIVELY_CLOSED_COOKIE_NAME,
      value,
      maxAgeDays: getters.channel.cookieMaxAgeInDays()
    })
  },
  welcomeMessageShownById (_: any, welcomeMessageId: number) {
    if (isNaN(welcomeMessageId)) return
    const welcomeMessageIds = alreadySeenWelcomeMessageIds()
    if (welcomeMessageIds.includes(welcomeMessageId.toString())) return

    welcomeMessageIds.unshift(welcomeMessageId.toString())
    setItem(SHOWN_WELCOME_MESSAGE_IDS_KEY, welcomeMessageIds.join(','))
  },
  welcomeMessageClick ({
    state,
    getters,
    dispatch
  }: { state: State, getters: any, dispatch: any }) {
    if (getters.isShowingOpenChat) dispatch('welcomeMessageClosed')
    requestMessageHistory()
    sendWelcomeMessageClicked(state.welcomeMessage?.id, getQuery('chatbotPayload'))
  },
  exitIntent ({
    commit,
    state,
    rootState
  }: { commit: any, state: State, rootState: any }) {
    if (rootState.bubble?.hasWebsiteChatBeenOpened) return

    const onExitWelcomeMessage = getValidExitWelcomeMessage(state.welcomeMessages, rootState.parentUrl)

    if (onExitWelcomeMessage) {
      commit('setWelcomeMessage', onExitWelcomeMessage)
      commit('setForceShowWelcomeMessage', { value: true })
    }
  },
  redirectWelcomeMessageToWizard ({
    state,
    getters,
    rootState
  }: { state: State, getters: any, rootState: any }): boolean {
    if (!getters.isShowingWelcomeMessage && !getters.isShowingOpenChat) return false
    const wizardUrl = state.welcomeMessage?.wizardUrl
    if (!wizardUrl) return false

    window.parent.location.href = addQueryToURL(wizardUrl, { subscriber_id: rootState.subscriberId })
    return true
  }
}
